import { markModalAsViewed } from '@/pages/api/scribeAPIV2';
import { type ModalView } from '@/types';
import { logger } from '@/utils/logger/logger';
import { ModalViewAction } from '@heidi/schema/ml-scribe';
import { type UserResponse } from '@heidi/workspace/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { GET_USER } from '../query/useQueryUser';

export const MARK_MODAL_AS_VIEWED_MUTATION_KEY = 'markModalAsViewed';

export const useMutateMarkModalAsViewed = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [MARK_MODAL_AS_VIEWED_MUTATION_KEY],
    mutationFn: markModalAsViewed,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [GET_USER] });

      const previousUser = queryClient.getQueryData<UserResponse>([GET_USER]);

      if (previousUser) {
        const updatedUser = produce(previousUser, (draft) => {
          const now = new Date().toISOString();
          if (!draft.seen_modals) {
            draft.seen_modals = {};
          }
          const seenModals = draft.seen_modals as Record<string, ModalView>;
          const existing = seenModals[variables.modal_id] as ModalView | undefined;
          // BE contract: `hide_forever` is only applied when explicitly provided.
          // Pass-through (`undefined` / `null`) leaves the existing value untouched.
          const nextHideForever =
            variables.hide_forever == null
              ? (existing?.hide_forever ?? false)
              : variables.hide_forever;
          if (variables.action === ModalViewAction.dismiss) {
            // Do not bump `view_count` on dismiss — mount / legacy increment / engage
            // already record impressions; counting dismiss too double-counted one
            // consult for `view_count - engage_count` rules (e.g. export banner).
            seenModals[variables.modal_id] = {
              ...existing,
              modal_id: variables.modal_id,
              first_seen_at: existing?.first_seen_at ?? now,
              last_seen_at: now,
              view_count: existing?.view_count ?? 0,
              consecutive_dismiss_count: (existing?.consecutive_dismiss_count ?? 0) + 1,
              engage_count: existing?.engage_count ?? 0,
              last_engaged_at: existing?.last_engaged_at ?? null,
              hide_forever: nextHideForever,
            };
          } else if (variables.action === ModalViewAction.engage) {
            seenModals[variables.modal_id] = {
              ...existing,
              modal_id: variables.modal_id,
              first_seen_at: existing?.first_seen_at ?? now,
              last_seen_at: now,
              view_count: (existing?.view_count ?? 0) + 1,
              consecutive_dismiss_count: 0,
              engage_count: (existing?.engage_count ?? 0) + 1,
              last_engaged_at: now,
              hide_forever: nextHideForever,
            };
          } else {
            // Legacy behavior. Mirrors the BE contract: increment_count=false
            // only records the first view (no-op for repeat views) so callers
            // like dismiss() don't inflate view_count past what the BE stores.
            const isFirstView = !existing;
            const shouldIncrement = variables.increment_count !== false || isFirstView;
            seenModals[variables.modal_id] = {
              ...existing,
              modal_id: variables.modal_id,
              first_seen_at: existing?.first_seen_at ?? now,
              last_seen_at: now,
              view_count: shouldIncrement
                ? (existing?.view_count ?? 0) + 1
                : (existing?.view_count ?? 0),
              consecutive_dismiss_count: existing?.consecutive_dismiss_count ?? 0,
              engage_count: existing?.engage_count ?? 0,
              last_engaged_at: existing?.last_engaged_at ?? null,
              hide_forever: nextHideForever,
            };
          }
        });

        queryClient.setQueryData<UserResponse>([GET_USER], updatedUser);
      }

      return { previousUser };
    },
    onSuccess: async (response) => {
      const previousUser = queryClient.getQueryData<UserResponse>([GET_USER]);
      if (previousUser && response.data.seen_modals) {
        const updatedUser = produce(previousUser, (draft) => {
          draft.seen_modals = response.data.seen_modals;
        });

        queryClient.setQueryData<UserResponse>([GET_USER], updatedUser);
      }

      await queryClient.invalidateQueries({ queryKey: [GET_USER] });
    },
    onError: (error, variables, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData([GET_USER], context.previousUser);
        logger.error('[Modal Queue] Rolled back optimistic update due to error', error);
      } else {
        logger.error('Failed to mark modal as viewed', error);
      }
    },
  });
};
